'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import {
  Banknote,
  BedDouble,
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  MapPin,
  RotateCcw,
  Ruler,
  Search,
  SearchX,
  X,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore, useKeywordsStore } from '@/lib/store'
import { api } from '@/lib/api'
import { useI18n } from '@/lib/i18n'
import { Chip, Seg, RMenu, IconBtn, REmptyState, pillBtnBase, useIsMobile } from '@/components/roostr'
import { useFeatureGroups } from './use-feature-groups'
import {
  DualRange,
  RoomsRange,
  SALE_PRICE_MAX,
  SALE_PRICE_STEP,
  fmtK,
  linearScale,
  saleScale,
  parseAreaInput,
  parsePriceInput,
} from './range-controls'
import type { Location, TransactionType } from '@/lib/types'
import { ROOMS_MAX, PRICE_MIN, PRICE_MAX, PRICE_STEP, AREA_MIN, AREA_MAX, AREA_STEP } from '@/lib/constants'

interface FilterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onApply?: () => void
  /** Section anchor to scroll to on open ('location' | 'rooms' | 'price' |
      'area' | …) — lets the mobile quick-filter pills open the full modal
      already positioned at their topic instead of spawning mini-sheets. */
  initialSection?: string
}

// Dual-thumb price/area slider + tap-to-range rooms stepper now live in
// range-controls.tsx — the MobileSearchSheet renders the same two controls,
// so they can't be private to this file any more.

export function FilterModal({ open, onOpenChange, onApply, initialSection }: FilterModalProps) {
  const { t, uiLanguage } = useI18n()
  const {
    activeTags,
    toggleTag,
    clearTags,
    filters,
    updateFilter,
    selectedCity,
    setSelectedCity,
    selectedDistricts,
    setSelectedDistricts,
    filterCount,
    setFilterCount,
    isCountLoading,
    setIsCountLoading,
  } = useSearchStore()
  const { keywords, filterTags, isLoading: keywordsLoading } = useKeywordsStore()

  const mobile = useIsMobile()
  const [mounted, setMounted] = React.useState(false)

  // Search-within-filters state
  const [filterSearchQuery, setFilterSearchQuery] = React.useState('')

  // Body scroll container + section anchors for the left rail
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const secRefs = React.useRef<Record<string, HTMLElement | null>>({})

  // ----- Live result count (debounced, aborted) — same mechanism as before -----
  const countDebounceRef = React.useRef<NodeJS.Timeout>(null)
  const countAbortRef = React.useRef<AbortController>(null)

  React.useEffect(() => {
    if (!open) return

    if (countDebounceRef.current) clearTimeout(countDebounceRef.current)
    setIsCountLoading(true)

    countDebounceRef.current = setTimeout(async () => {
      if (countAbortRef.current) countAbortRef.current.abort()
      const abortController = new AbortController()
      countAbortRef.current = abortController

      try {
        const response = await api.countListings(
          {
            filters: {
              ...filters,
              city: selectedCity || undefined,
              districts: selectedDistricts.length > 0 ? selectedDistricts : undefined,
            },
            tags: activeTags.length > 0 ? activeTags : undefined,
          },
          abortController.signal,
        )
        if (!abortController.signal.aborted) {
          setFilterCount(response.total_count)
          setIsCountLoading(false)
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Count request failed:', error)
          setFilterCount(null)
          setIsCountLoading(false)
        }
      }
    }, 400)

    return () => {
      if (countDebounceRef.current) clearTimeout(countDebounceRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filters, activeTags, selectedCity, selectedDistricts])

  // Clean up on modal close
  React.useEffect(() => {
    if (!open) {
      if (countAbortRef.current) countAbortRef.current.abort()
      if (countDebounceRef.current) clearTimeout(countDebounceRef.current)
      setFilterSearchQuery('')
    }
  }, [open])

  // ----- Cities (real API, as before) -----
  const [cities, setCities] = React.useState<string[]>([])
  const [citiesLoading, setCitiesLoading] = React.useState(true)

  React.useEffect(() => {
    const fetchCities = async () => {
      try {
        const data = await api.getCities()
        // Alphabetical (Czech collation) so the dropdown is scannable.
        setCities([...data].sort((a, b) => a.localeCompare(b, 'cs')))
      } catch (error) {
        console.error('Failed to fetch cities:', error)
        setCities(['Praha']) // Fallback
      } finally {
        setCitiesLoading(false)
      }
    }
    fetchCities()
  }, [])

  // ----- District typeahead (real API, as before) -----
  const [districtSearchQuery, setDistrictSearchQuery] = React.useState('')
  const [locations, setLocations] = React.useState<Location[]>([])
  const [districtsLoading, setDistrictsLoading] = React.useState(false)
  const districtDebounceRef = React.useRef<NodeJS.Timeout>(null)

  React.useEffect(() => {
    if (!districtSearchQuery.trim()) {
      setLocations([])
      return
    }
    if (districtDebounceRef.current) clearTimeout(districtDebounceRef.current)
    districtDebounceRef.current = setTimeout(async () => {
      setDistrictsLoading(true)
      try {
        const results = await api.getLocations(selectedCity, districtSearchQuery, 15)
        setLocations(results)
      } catch (error) {
        console.error('Failed to fetch locations:', error)
        setLocations([])
      } finally {
        setDistrictsLoading(false)
      }
    }, 300)
    return () => {
      if (districtDebounceRef.current) clearTimeout(districtDebounceRef.current)
    }
  }, [districtSearchQuery, selectedCity])

  const addDistrict = (district: string) => {
    if (!selectedDistricts.includes(district)) {
      setSelectedDistricts([...selectedDistricts, district])
    }
    setDistrictSearchQuery('')
  }

  const removeDistrict = (district: string) => {
    setSelectedDistricts(selectedDistricts.filter((d) => d !== district))
  }

  // Resolve display names from the locations API (name may be a nested object)
  const districtHits = React.useMemo(() => {
    const seen = new Set<string>()
    const hits: { name: string; type?: string }[] = []
    for (const location of locations) {
      let nameValue: unknown = location.name
      if (typeof nameValue === 'object' && nameValue !== null) {
        const input = (nameValue as { input?: string | string[] }).input
        nameValue = Array.isArray(input) ? input[0] : input || ''
      }
      const displayName = String(nameValue || location.district)
      if (!displayName || seen.has(displayName) || selectedDistricts.includes(displayName)) continue
      seen.add(displayName)
      hits.push({ name: displayName, type: location.type })
    }
    return hits
  }, [locations, selectedDistricts])

  // ----- Transaction / price bounds -----
  const isSale = filters.transaction_type === 'sale'
  const priceMax = isSale ? SALE_PRICE_MAX : PRICE_MAX
  const priceStep = isSale ? SALE_PRICE_STEP : PRICE_STEP

  const handleTransactionChange = (v: TransactionType) => {
    if ((filters.transaction_type || 'rent') === v) return
    updateFilter('transaction_type', v)
    // Rent and sale price ranges are incompatible — reset price bounds
    updateFilter('price_min', undefined)
    updateFilter('price_max', undefined)
  }

  // Always-running guard: rent and sale price scales are incompatible, so a
  // transaction-type flip from ANY source (this modal's Seg, result chips,
  // URL sync, text-search parse) must clear stale price bounds. This effect
  // runs even while the modal is closed — FilterModal stays mounted via
  // FiltersButton and only returns null after its hooks. The ref is
  // initialized to the current value so it never fires on first mount.
  const prevIsSaleRef = React.useRef(isSale)
  React.useEffect(() => {
    if (prevIsSaleRef.current === isSale) return
    prevIsSaleRef.current = isSale
    if (filters.price_min != null) updateFilter('price_min', undefined)
    if (filters.price_max != null) updateFilter('price_max', undefined)
  }, [isSale, filters.price_min, filters.price_max, updateFilter])

  // ----- Rooms (derived from store; design tap-to-range semantics) -----
  const roomsLo = filters.rooms_min ?? (filters.rooms_max != null ? 1 : null)
  const roomsHi = roomsLo == null ? null : Math.min(filters.rooms_max ?? ROOMS_MAX, ROOMS_MAX)

  const handleRoomsChange = ([lo, hi]: [number | null, number | null]) => {
    updateFilter('rooms_min', lo ?? undefined)
    updateFilter('rooms_max', hi == null || hi >= ROOMS_MAX ? undefined : hi)
  }

  // ----- Price / area (derived from store; undefined at extremes, as before) -----
  const priceValue: [number, number] = [filters.price_min ?? PRICE_MIN, filters.price_max ?? priceMax]
  const handlePriceChange = ([lo, hi]: [number, number]) => {
    updateFilter('price_min', lo === PRICE_MIN ? undefined : lo)
    updateFilter('price_max', hi === priceMax ? undefined : hi)
  }
  const formatPrice = (v: number) =>
    v === 0 ? '0 Kč' : v >= priceMax ? t('search.any', 'Any') : `${fmtK(v, isSale)} Kč`

  const areaValue: [number, number] = [filters.area_min ?? AREA_MIN, filters.area_max ?? AREA_MAX]
  const handleAreaChange = ([lo, hi]: [number, number]) => {
    updateFilter('area_min', lo === AREA_MIN ? undefined : lo)
    updateFilter('area_max', hi === AREA_MAX ? undefined : hi)
  }
  const formatArea = (v: number) => (v >= AREA_MAX ? t('search.any', 'Any') : `${v} m²`)

  // Price mode (rent only vs incl. utilities) — writes the same store field
  // the previous modal's PriceModeToggle wrote (default is total).
  const useTotal = filters.use_total_price !== false

  // ----- Feature keyword groups from the keywords pipeline -----
  // Shared with MobileSearchSheet (use-feature-groups.ts) so both surfaces
  // render the same full catalogue.
  const groups = useFeatureGroups()

  // ----- Active-filter count (same semantics as before) -----
  const locationFilterCount =
    (filters.type && filters.type !== 'apartment' ? 1 : 0) +
    (filters.transaction_type && filters.transaction_type !== 'rent' ? 1 : 0) +
    (selectedCity && selectedCity !== 'Praha' ? 1 : 0) +
    (selectedDistricts.length > 0 ? 1 : 0)

  const numericFilterCount =
    (filters.rooms_min ? 1 : 0) + (filters.rooms_max ? 1 : 0) +
    (filters.area_min ? 1 : 0) + (filters.area_max ? 1 : 0) +
    (filters.price_min ? 1 : 0) + (filters.price_max ? 1 : 0)

  // Non-default price mode ("Rent only") counts as an active filter so the
  // rail dot, the header count badge, and Clear-all enablement agree.
  const priceModeCount = filters.use_total_price === false ? 1 : 0

  const activeCount = activeTags.length + numericFilterCount + locationFilterCount + priceModeCount

  // ----- Search-within-filters (filters.jsx secMatch concept) -----
  const query = filterSearchQuery.trim().toLowerCase()
  const secMatch = (label: string, extra: string[] = []) =>
    !query || label.toLowerCase().includes(query) || extra.some((k) => k.toLowerCase().includes(query))
  const chipMatch = (label: string) => !query || label.toLowerCase().includes(query)

  const labels = {
    location: t('filterModal.locationType', 'Location & type'),
    rooms: t('search.rooms', 'Rooms'),
    price: t('search.price', 'Price'),
    area: t('search.area', 'Area'),
  }

  // Section registry for the rail + visibility: [id, label, Icon, hasActive, visible]
  const sections: [string, string, LucideIcon, boolean, boolean][] = [
    [
      'location',
      labels.location,
      MapPin,
      locationFilterCount > 0,
      secMatch(labels.location, [
        'location', 'type', 'city', 'district', 'rent', 'sale', 'property',
        'lokalita', 'typ', 'město', 'mesto', 'čtvrť', 'ctvrt', 'čtvrti',
        'pronájem', 'pronajem', 'prodej',
        ...cities,
        ...selectedDistricts,
      ]),
    ],
    [
      'rooms',
      labels.rooms,
      BedDouble,
      filters.rooms_min != null || filters.rooms_max != null,
      secMatch(labels.rooms, ['rooms', 'pokoje', 'pokoj', 'místnosti', 'mistnosti', 'dispozice']),
    ],
    [
      'price',
      labels.price,
      Banknote,
      filters.price_min != null || filters.price_max != null || filters.use_total_price === false,
      secMatch(labels.price, ['price', 'utilities', 'total', 'cena', 'kč', 'czk', 'nájem', 'najem', 'energie', 'celkem']),
    ],
    [
      'area',
      labels.area,
      Ruler,
      filters.area_min != null || filters.area_max != null,
      secMatch(labels.area, ['area', 'size', 'm2', 'm²', 'plocha', 'výměra', 'vymera', 'metry']),
    ],
    ...groups.map((g): [string, string, LucideIcon, boolean, boolean] => [
      g.id,
      g.label,
      g.Icon,
      g.items.some((it) => activeTags.includes(it.id)),
      secMatch(g.label, g.items.map((it) => it.label)),
    ]),
  ]

  const visibleById: Record<string, boolean> = {}
  sections.forEach(([id, , , , visible]) => { visibleById[id] = visible })

  const noneVisible = query.length > 0 && sections.every((s) => !s[4])

  const jump = (id: string) => {
    const el = secRefs.current[id]
    const body = bodyRef.current
    if (el && body) body.scrollTo({ top: el.offsetTop - 14, behavior: 'smooth' })
  }

  // Quick-filter pills open the modal pre-scrolled to their section. Runs
  // after the portal content mounts (rAF); instant jump — smooth-scrolling
  // from the top on every open would read as motion noise.
  React.useEffect(() => {
    if (!open || !initialSection) return
    const raf = requestAnimationFrame(() => {
      const el = secRefs.current[initialSection]
      const body = bodyRef.current
      if (el && body) body.scrollTo({ top: el.offsetTop - 14 })
    })
    return () => cancelAnimationFrame(raf)
  }, [open, initialSection])

  // ----- Clear all / apply (same behavior as before) -----
  const handleClearAll = () => {
    clearTags()
    updateFilter('rooms_min', undefined)
    updateFilter('rooms_max', undefined)
    updateFilter('area_min', undefined)
    updateFilter('area_max', undefined)
    updateFilter('price_min', undefined)
    updateFilter('price_max', undefined)
    updateFilter('use_total_price', true) // store default
    updateFilter('type', 'apartment')
    updateFilter('transaction_type', 'rent')
    setSelectedCity('Praha')
    setSelectedDistricts([])
    setDistrictSearchQuery('')
  }

  const handleApply = () => {
    onOpenChange(false)
    onApply?.()
  }

  // ----- Modal chrome: portal mount, body scroll lock, Esc -----
  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onOpenChange])

  if (!mounted || !open) return null

  // Footer label — 'Show N homes' with Czech pluralization
  const showLabel = (() => {
    if (filterCount == null) return t('filterModal.showResults', 'Show results')
    const n = filterCount
    let key = 'filterModal.showHomesMany'
    if (n === 1) key = 'filterModal.showHomesOne'
    else if (uiLanguage === 'cs' && n >= 2 && n <= 4) key = 'filterModal.showHomesFew'
    return t(key, 'Show {n} homes').replace('{n}', n.toLocaleString('cs-CZ'))
  })()

  const keywordsPending = keywordsLoading && groups.length === 0

  // Section shell — filters.jsx:183-188 (16px vertical padding, hairline
  // divider, 10.5/700 uppercase label). Plain function call (not a component)
  // so inputs inside sections keep focus across re-renders.
  const sec = (id: string, label: string, children: React.ReactNode) => (
    <section
      key={id}
      ref={(el) => { secRefs.current[id] = el }}
      className="border-b border-outline-variant py-4"
    >
      <div className="mb-[10px] flex items-center gap-[6px] text-[10.5px] font-bold tracking-[0.7px] text-ink-muted">
        {label.toUpperCase()}
      </div>
      {children}
    </section>
  )

  const searchPillCls =
    'flex items-center gap-[7px] rounded-full border border-outline bg-surface-low px-[13px] py-2'

  const modalContent = (
    <div
      onClick={() => onOpenChange(false)}
      className={cn(
        'fixed inset-0 z-[100] flex justify-center bg-[rgba(26,23,23,0.42)]',
        mobile ? 'items-end p-0' : 'items-center p-6'
      )}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'flex max-w-full animate-card-in flex-col overflow-hidden bg-surface shadow-e3',
          mobile ? 'w-full rounded-t-[24px]' : 'rounded-shape-xl'
        )}
        style={{
          width: mobile ? '100%' : 780,
          maxHeight: mobile ? '92vh' : 'min(680px, 90vh)',
        }}
      >
        {/* Header — title + count badge, search-within-filters, close */}
        <div
          className={cn(
            'flex flex-shrink-0 items-center gap-3 border-b border-outline-variant',
            mobile ? 'px-4 pb-3 pt-4' : 'px-[22px] pb-[14px] pt-[18px]'
          )}
        >
          <h2 className="flex items-center gap-2 text-[19px] font-bold tracking-[-0.3px] text-ink">
            {t('search.filters', 'Filters')}
            {activeCount > 0 && (
              <span className="rounded-full bg-primary px-2 py-[2px] font-mono text-[11px] font-bold text-white">
                {activeCount}
              </span>
            )}
          </h2>
          <div className="flex flex-1 justify-end">
            <div className={cn(searchPillCls, mobile ? 'w-full' : 'w-[260px]')}>
              <Search size={15} className="shrink-0 text-ink-muted" />
              <input
                type="text"
                value={filterSearchQuery}
                onChange={(e) => setFilterSearchQuery(e.target.value)}
                placeholder={t('filterModal.searchFilters', 'Search filters…')}
                className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
              />
              {filterSearchQuery && (
                <IconBtn size={18} onClick={() => setFilterSearchQuery('')} aria-label={t('filterModal.close', 'Close')}>
                  <X size={13} className="text-ink-muted" />
                </IconBtn>
              )}
            </div>
          </div>
          <IconBtn
            size={34}
            className="bg-surface-low"
            onClick={() => onOpenChange(false)}
            title={t('filterModal.close', 'Close')}
            aria-label={t('filterModal.close', 'Close')}
          >
            <X size={17} className="text-ink-secondary" />
          </IconBtn>
        </div>

        {/* Mobile section nav — the desktop left rail's counterpart, so
            phones see the FULL section map (all 17 categories render below;
            without this row only the first screenful reads as "available").
            Same data + jump + active-dot semantics, horizontal instead of
            vertical. Pinned between header and body, never scrolls away. */}
        {mobile && (
          <div className="flex flex-shrink-0 items-center gap-1.5 overflow-x-auto scrollbar-hide border-b border-outline-variant px-4 py-2.5">
            {sections.filter((s) => s[4]).map(([id, label, Icon, has]) => (
              <button
                key={id}
                type="button"
                onClick={() => jump(id)}
                className="flex flex-shrink-0 items-center gap-1.5 rounded-full border border-outline bg-surface px-3 py-[7px] text-[12px] font-semibold text-ink-secondary transition-transform active:scale-[0.97]"
              >
                <Icon size={14} className="shrink-0 text-ink-muted" />
                <span className="whitespace-nowrap">{label}</span>
                {has && <span className="h-[5px] w-[5px] flex-shrink-0 rounded-full bg-primary" />}
              </button>
            ))}
          </div>
        )}

        {/* Body: desktop-only left rail + scrollable sections */}
        <div className="flex min-h-0 flex-1">
          {!mobile && (
            <div className="w-[190px] flex-shrink-0 overflow-y-auto border-r border-outline-variant px-2 py-3">
              {sections.filter((s) => s[4]).map(([id, label, Icon, has]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => jump(id)}
                  className="flex w-full items-center gap-2 rounded-shape-xs bg-transparent px-[10px] py-2 text-left text-[12.5px] font-semibold text-ink-secondary transition-colors hover:bg-surface-low"
                >
                  <Icon size={16} className="shrink-0 text-ink-muted" />
                  <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
                  {has && <span className="h-[6px] w-[6px] flex-shrink-0 rounded-full bg-primary" />}
                </button>
              ))}
            </div>
          )}

          <div ref={bodyRef} className={cn('relative min-w-0 flex-1 overflow-y-auto', mobile ? 'px-4' : 'px-[22px]')}>
            {/* Location & type */}
            {visibleById.location &&
              sec(
                'location',
                labels.location,
                <>
                  <div className="flex flex-wrap items-center gap-[14px]">
                    <Seg<TransactionType>
                      value={(filters.transaction_type || 'rent') as TransactionType}
                      onChange={handleTransactionChange}
                      options={[
                        { value: 'rent', label: t('search.rent', 'Rent') },
                        { value: 'sale', label: t('search.sale', 'Sale') },
                      ]}
                      className="flex-none"
                    />
                    <RMenu
                      width={200}
                      button={(menuOpen, toggle) => (
                        <button
                          type="button"
                          onClick={() => { if (!citiesLoading) toggle() }}
                          className={cn(
                            pillBtnBase,
                            'border border-outline bg-surface py-2 pl-[14px] pr-3 text-[13px] text-ink',
                            citiesLoading && 'cursor-not-allowed opacity-50'
                          )}
                        >
                          <Building2 size={15} className="text-ink-secondary" />
                          {selectedCity}
                          {menuOpen ? (
                            <ChevronUp size={16} className="text-ink-muted" />
                          ) : (
                            <ChevronDown size={16} className="text-ink-muted" />
                          )}
                        </button>
                      )}
                      items={cities.map((c) => ({
                        label: c,
                        active: selectedCity === c,
                        onClick: () => {
                          setSelectedCity(c)
                          setSelectedDistricts([])
                          setDistrictSearchQuery('')
                        },
                      }))}
                    />
                  </div>
                  <div className="mt-[14px]">
                    <div className="mb-[7px] text-[12px] text-ink-secondary">
                      {t('filterModal.districts', 'Districts')}{' '}
                      {selectedDistricts.length > 0 &&
                        t('filterModal.selectedCount', '({n} selected)').replace('{n}', String(selectedDistricts.length))}
                    </div>
                    {selectedDistricts.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-[6px]">
                        {selectedDistricts.map((d) => (
                          <Chip
                            key={d}
                            label={d}
                            icon={<X size={13} />}
                            active
                            onClick={() => removeDistrict(d)}
                            title={t('filterModal.removeDistrict', 'Remove district')}
                          />
                        ))}
                      </div>
                    )}
                    <div className="relative max-w-[320px]">
                      <div className={searchPillCls}>
                        <Search size={15} className="shrink-0 text-ink-muted" />
                        <input
                          type="text"
                          value={districtSearchQuery}
                          onChange={(e) => setDistrictSearchQuery(e.target.value)}
                          placeholder={t('filterModal.searchDistrictsIn', 'Search districts in {city}…').replace('{city}', selectedCity)}
                          className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
                        />
                      </div>
                      {districtSearchQuery.trim() && (
                        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 overflow-hidden rounded-shape-sm border border-outline bg-surface shadow-e2">
                          {districtsLoading ? (
                            <div className="flex items-center justify-center py-3">
                              <Loader2 size={16} className="animate-spin text-ink-muted" />
                            </div>
                          ) : districtHits.length === 0 ? (
                            <div className="px-[13px] py-[9px] text-center text-[13px] text-ink-muted">
                              {t('search.noDistrictsFound', 'No districts found')}
                            </div>
                          ) : (
                            districtHits.map((d) => (
                              <button
                                key={d.name}
                                type="button"
                                onClick={() => addDistrict(d.name)}
                                className="flex w-full items-center gap-2 bg-transparent px-[13px] py-[9px] text-left text-[13px] text-ink transition-colors hover:bg-surface-low"
                              >
                                <span className="min-w-0 flex-1 truncate">{d.name}</span>
                                {d.type && d.type !== 'district' && (
                                  <span className="text-[11px] text-ink-muted">{d.type}</span>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

            {/* Rooms */}
            {visibleById.rooms &&
              sec('rooms', labels.rooms, <RoomsRange lo={roomsLo} hi={roomsHi} onChange={handleRoomsChange} />)}

            {/* Price */}
            {visibleById.price &&
              sec(
                'price',
                labels.price,
                <>
                  {!isSale && (
                    <div className="mb-[14px] flex items-center gap-[10px]">
                      <span className="text-[12px] text-ink-secondary">{t('filterModal.filterBy', 'Filter by')}</span>
                      <Seg<'rent' | 'total'>
                        value={useTotal ? 'total' : 'rent'}
                        onChange={(v) => updateFilter('use_total_price', v === 'total')}
                        options={[
                          { value: 'rent', label: t('filterModal.rentOnly', 'Rent only') },
                          { value: 'total', label: t('filterModal.inclUtilities', 'Incl. utilities') },
                        ]}
                        className="flex-none"
                      />
                      <span
                        title={t('search.estTotalTooltip', 'Estimated total includes rent, utilities, service charges, parking, and internet — extracted by AI from listing descriptions. Listings without this data use base rent.')}
                        className="text-ink-muted"
                      >
                        <Info size={14} />
                      </span>
                    </div>
                  )}
                  <DualRange
                    min={PRICE_MIN}
                    max={priceMax}
                    step={priceStep}
                    value={priceValue}
                    onChange={handlePriceChange}
                    format={formatPrice}
                    scale={isSale ? saleScale : linearScale}
                    inputFormat={(v) => (v >= priceMax ? t('search.any', 'Any') : v === 0 ? '0' : fmtK(v, isSale))}
                    inputParse={parsePriceInput}
                  />
                </>
              )}

            {/* Area */}
            {visibleById.area &&
              sec(
                'area',
                labels.area,
                <DualRange
                  min={AREA_MIN}
                  max={AREA_MAX}
                  step={AREA_STEP}
                  value={areaValue}
                  onChange={handleAreaChange}
                  format={formatArea}
                  inputFormat={(v) => (v >= AREA_MAX ? t('search.any', 'Any') : String(v))}
                  inputParse={parseAreaInput}
                />
              )}

            {/* Feature keyword groups */}
            {keywordsPending ? (
              <div className="flex items-center justify-center gap-3 py-12 text-ink-muted">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-[13px]">{t('filterModal.loadingFilters', 'Loading filters…')}</span>
              </div>
            ) : (
              groups.map((g) => {
                if (!visibleById[g.id]) return null
                const items =
                  query && !g.label.toLowerCase().includes(query)
                    ? g.items.filter((it) => chipMatch(it.label))
                    : g.items
                return sec(
                  g.id,
                  g.label,
                  <div className="flex flex-wrap gap-[7px]">
                    {items.map((it) => {
                      const active = activeTags.includes(it.id)
                      return (
                        <Chip
                          key={it.id}
                          label={it.label}
                          icon={active ? <Check size={13} /> : undefined}
                          active={active}
                          onClick={() => toggleTag(it.id)}
                        />
                      )
                    })}
                  </div>
                )
              })
            )}

            {noneVisible && (
              <REmptyState
                icon={<SearchX size={30} />}
                title={t('filterModal.noFiltersFound', 'No filters found')}
                text={t('filterModal.tryDifferentTerm', 'Try a different search term.')}
              />
            )}
            <div className="h-[14px]" />
          </div>
        </div>

        {/* Footer — Clear all + primary Show N homes */}
        <div
          className={cn(
            'flex flex-shrink-0 items-center justify-between gap-[10px] border-t border-outline-variant',
            mobile ? 'px-4 pt-3' : 'px-[22px] py-[14px]'
          )}
          style={mobile ? { paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' } : undefined}
        >
          <button
            type="button"
            onClick={handleClearAll}
            disabled={activeCount === 0}
            className={cn(
              pillBtnBase,
              'bg-transparent px-[14px] py-[10px] text-[13px] text-ink-secondary',
              activeCount === 0 && 'opacity-50'
            )}
          >
            <RotateCcw size={16} />
            {t('search.clearAll', 'Clear all')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className={cn(pillBtnBase, 'min-w-[160px] justify-center bg-primary px-[22px] py-[11px] text-[14px] text-white shadow-e1')}
          >
            {isCountLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t('filterModal.showResults', 'Show results')}
              </>
            ) : (
              showLabel
            )}
          </button>
        </div>
      </div>
    </div>
  )

  // Use portal to render at document body
  return createPortal(modalContent, document.body)
}
